import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import List from '@pelckmans/elementis/components/list';
import ListGroup from '@pelckmans/elementis/components/list-group';
import Section from '@pelckmans/elementis/components/section';

import { Medialink } from '../../../api/medialink';
import { Module } from '../../../api/module';
import { SlideSet } from '../../../api/slide-set';
import { NodeWithoutDescription } from '../../../api/table-of-content';
import { useRoutes } from '../../../hooks/useRoutes';
import EmptyState from '../../../shared-components/EmptyState';
import LoadingState from '../../../shared-components/LoadingState';
import { getDescriptionOfNode } from '../../../utils/getNodeDescription';
import { getPillGroup } from '../../material/categorical/browse-mode/ResultsForModule';
import MedialinkItem from '../../material/components/MedialinkItem';
import SlideSetItem from '../../material/components/SlideSetItem';

const BASE = 'PAGES.LINKED_MATERIAL_LIST';

interface Props {
  module?: Module;
  superModule?: Module;
  loading: boolean;
  medialinksByNodeId?: Record<string, Medialink[]>;
  slideSetsByNodeId?: Record<string, SlideSet[]>;
  flattenedTocNodes?: NodeWithoutDescription[];
  refetch: () => {};
}

export default function MaterialSection({
  medialinksByNodeId,
  slideSetsByNodeId,
  module,
  superModule,
  flattenedTocNodes,
  loading,
  refetch,
}: Props) {
  const { t } = useTranslation();
  const { buildAbsolutePath } = useRoutes();
  const [searchParams] = useSearchParams();

  function renderContent() {
    const nodesWithMaterial =
      flattenedTocNodes
        ?.map(node => ({
          node,
          medialinks: medialinksByNodeId?.[node.id] ?? [],
          slideSets: slideSetsByNodeId?.[node.id] ?? [],
        }))
        .filter(({ medialinks, slideSets }) => medialinks.length > 0 || slideSets.length > 0) ?? [];

    if (loading) {
      return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} />;
    }

    if (!module || !superModule || nodesWithMaterial.length === 0) {
      return <EmptyState variant="list" headerTitle={t(`${BASE}.EMPTY.TITLE`)} bodyText={t(`${BASE}.EMPTY.BODY`)} />;
    }

    const { subjectSlug, methodSlug, slug } = superModule;
    const medialinks = searchParams.get('medialinkIds')?.split(',');
    const openMedialinkId = medialinks?.length === 1 ? medialinks[0] : undefined;

    return (
      <ListGroup>
        {nodesWithMaterial.map(({ node, medialinks, slideSets }) => {
          const link = buildAbsolutePath(['MATERIAL', 'MATERIAL.CONTENT', 'MATERIAL.CONTENT.TOC'], {
            subjectSlug,
            methodSlug,
            moduleSlug: slug,
            selectedModuleSlug: module.slug,
            nodeId: node.id,
          });

          return (
            <List key={node.id}>
              <List.Header
                title={getDescriptionOfNode(node, t)}
                titleTag="h3"
                prominent
                linkProps={{
                  children: t(`${BASE}.LINK`),
                  to: link,
                }}
              />
              {slideSets.map(slideSet => (
                <SlideSetItem
                  key={`${slideSet.id}-${slideSet.versionId}`}
                  node={node}
                  module={module}
                  slideSet={slideSet}
                  refetch={refetch}
                  showActionMenu={false}
                />
              ))}
              {medialinks.map(medialink => (
                <MedialinkItem
                  key={medialink.id}
                  medialink={medialink}
                  getPillGroup={getPillGroup}
                  node={node}
                  module={module}
                  refetch={refetch}
                  showActionMenu={false}
                  openMedialinkId={openMedialinkId}
                />
              ))}
            </List>
          );
        })}
      </ListGroup>
    );
  }

  return (
    <Section
      title={module?.niceName ?? ''}
      avatar={
        <Avatar>
          <AvatarImage name={module?.niceName ?? ''} src={module?.styling?.logo} />
        </Avatar>
      }
    >
      {renderContent()}
    </Section>
  );
}
